import {useCallback, useEffect, useRef, useState} from 'react';

import {assert} from 'Asserts';
import runModal from 'ui-library/runModal';

import FlowchartConfirmExitWithUnsavedChangesModal, {
  FlowchartConfirmExitWithUnsavedChangesModalProps,
} from '../modals/FlowchartConfirmExitWithUnsavedChangesModal';
import {FlowchartNode} from '../processFlowchartConfigEditorNodeTypes';

export interface SidepanelTabState {
  tabId: string;
  isDirty: boolean;
  onSave?: () => Promise<void> | void;
  onDiscard?: () => Promise<void> | void;
}

export type SidepanelRegisterTabState = Omit<SidepanelTabState, 'isDirty'>;

interface SidepanelState {
  node: FlowchartNode | null;
  fieldId: string | null;
  titleOverride: string | null;
}

interface UseFlowchartSidepanelUnsavedChangesReturn {
  registerTab: (params: SidepanelRegisterTabState) => void;
  unregisterTab: (tab: string) => void;
  setIsTabDirty: (tab: string, isDirty: boolean) => void;
  handleTabChange: (newTab: string, activeTab: string) => void;
  handleClose: () => void;
  getEffectiveState: () => SidepanelState;
}

export function useFlowchartSidepanelUnsavedChanges(
  onCloseSidepanel: () => void,
  selectedNode: FlowchartNode | null = null,
  selectedFieldId: string | null = null,
  titleOverride: string | null = null,
  onRevertNodeSelection?: (nodeId: string, fieldId: string | null) => void
): UseFlowchartSidepanelUnsavedChangesReturn {
  const tabStates = useRef<Map<string, SidepanelTabState>>(new Map());
  const [hasDirtyTabs, setHasDirtyTabs] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sidepanelState, setSidepanelState] = useState<SidepanelState>({
    node: null,
    fieldId: null,
    titleOverride: null,
  });
  const [incomingSidepanelState, setIncomingSidepanelState] = useState<SidepanelState | null>(null);

  const updateHasDirtyTabs = useCallback(() => {
    setHasDirtyTabs(Array.from(tabStates.current.values()).some((state) => state.isDirty));
  }, []);

  const clearAllTabStates = useCallback(() => {
    tabStates.current = new Map();
    setHasDirtyTabs(false);
    setSidepanelState({
      node: null,
      fieldId: null,
      titleOverride: null,
    });
    setIncomingSidepanelState(null);
  }, []);

  const registerTab = useCallback((params: SidepanelTabState) => {
    const {tabId, onSave, onDiscard} = params;
    const existingState = tabStates.current.get(tabId);

    if (existingState) {
      tabStates.current.set(tabId, {...existingState, onSave, onDiscard});
    } else {
      tabStates.current.set(tabId, {tabId, isDirty: false, onSave, onDiscard});
    }
  }, []);

  const unregisterTab = useCallback(
    (tabId: string) => {
      tabStates.current.delete(tabId);
      updateHasDirtyTabs();
    },
    [updateHasDirtyTabs]
  );

  const setIsTabDirty = useCallback(
    (tabId: string, dirty: boolean) => {
      const currentState = tabStates.current.get(tabId);
      if (currentState && currentState.isDirty !== dirty) {
        tabStates.current.set(tabId, {...currentState, isDirty: dirty});
        updateHasDirtyTabs();
      }
    },
    [updateHasDirtyTabs]
  );

  const discardAllChanges = useCallback(() => {
    tabStates.current.forEach((tabState) => {
      if (tabState.onDiscard) {
        tabState.onDiscard();
      }
    });

    clearAllTabStates();
  }, [clearAllTabStates]);

  const saveAllChanges = useCallback(() => {
    tabStates.current.forEach((tabState) => {
      if (tabState.onSave) {
        tabState.onSave();
      }
    });

    clearAllTabStates();
  }, [clearAllTabStates]);

  const markModalClosed = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleExitSidepanel = useCallback(
    (
      incomingNode: FlowchartNode | null,
      incomingFieldId: string | null,
      incomingTitleOverride: string | null
    ) => {
      if (incomingNode == null) {
        onCloseSidepanel();
      } else {
        // Render sidepanel for incoming state
        setSidepanelState({
          node: incomingNode,
          fieldId: incomingFieldId,
          titleOverride: incomingTitleOverride,
        });
      }
      // Clear incoming state
      setIncomingSidepanelState(null);
      markModalClosed();
    },
    [onCloseSidepanel, markModalClosed]
  );

  const showUnsavedChangesModal = useCallback(
    (incomingState: SidepanelState) => {
      if (isModalOpen) {
        // Prevent opening multiple modals
        return;
      }

      setIsModalOpen(true);
      runModal<FlowchartConfirmExitWithUnsavedChangesModalProps>(
        FlowchartConfirmExitWithUnsavedChangesModal,
        {
          onRequestClose: () => {
            // No-op, this is required by runModal to close the modal on non-submit operations
          },
          onRequestKeepEditing: () => {
            // When X is pressed, keep the current node's sidepanel open
            assert(
              sidepanelState.node != null && onRevertNodeSelection != null,
              'Node sidepanel open for and onRevertNodeSelection are required'
            );
            onRevertNodeSelection(sidepanelState.node.id, sidepanelState.fieldId);
            setIncomingSidepanelState(null);
            markModalClosed();
          },
          onRequestLeaveWithoutSaving: () => {
            discardAllChanges();
            handleExitSidepanel(incomingState.node, incomingState.fieldId, incomingState.titleOverride);
          },
          onRequestSubmit: () => {
            saveAllChanges();
            handleExitSidepanel(incomingState.node, incomingState.fieldId, incomingState.titleOverride);
          },
        }
      );
    },
    [
      isModalOpen,
      onRevertNodeSelection,
      markModalClosed,
      discardAllChanges,
      saveAllChanges,
      handleExitSidepanel,
      sidepanelState,
    ]
  );

  const handleTabChange = useCallback(
    (newTabId: string, activeTabId: string) => {
      if (newTabId === activeTabId) {
        return;
      }

      const currentState = tabStates.current.get(activeTabId);
      if (currentState?.isDirty) {
        showUnsavedChangesModal(sidepanelState);
      }
    },
    [showUnsavedChangesModal, sidepanelState]
  );

  const handleClose = useCallback(() => {
    if (hasDirtyTabs) {
      showUnsavedChangesModal(sidepanelState);
    } else {
      onCloseSidepanel();
    }
  }, [hasDirtyTabs, onCloseSidepanel, showUnsavedChangesModal, sidepanelState]);

  useEffect(() => {
    const currentState: SidepanelState = {
      node: selectedNode,
      fieldId: selectedFieldId,
      titleOverride,
    };

    // Check if we're in the middle of a modal flow
    if (incomingSidepanelState != null) {
      return;
    }

    // Check if the current state is the same as the sidepanel state
    const isSameState =
      currentState.node?.id === sidepanelState.node?.id &&
      currentState.fieldId === sidepanelState.fieldId &&
      currentState.titleOverride === sidepanelState.titleOverride;

    if (isSameState) {
      return;
    }

    // Case 1: Null -> Node (opening sidepanel)
    if (currentState.node && !sidepanelState.node) {
      setSidepanelState(currentState);
      return;
    }

    // Case 2: Node -> Null (closing sidepanel)
    if (!currentState.node && sidepanelState.node) {
      if (hasDirtyTabs) {
        showUnsavedChangesModal(currentState);
        return;
      } else {
        clearAllTabStates();
        return;
      }
    }

    // Case 3: Node -> Different Node (switching nodes)
    if (currentState.node && sidepanelState.node && currentState.node.id !== sidepanelState.node.id) {
      if (hasDirtyTabs) {
        showUnsavedChangesModal(currentState);
        return;
      } else {
        setSidepanelState(currentState);
        return;
      }
    }

    // Case 4: Same node, different field or title (updating within same node)
    if (currentState.node && sidepanelState.node && currentState.node.id === sidepanelState.node.id) {
      if (hasDirtyTabs) {
        showUnsavedChangesModal(currentState);
      } else {
        setSidepanelState(currentState);
      }
    }
  }, [
    selectedNode,
    selectedFieldId,
    titleOverride,
    sidepanelState,
    incomingSidepanelState,
    hasDirtyTabs,
    showUnsavedChangesModal,
    clearAllTabStates,
  ]);

  const getEffectiveState = useCallback(() => sidepanelState, [sidepanelState]);

  return {
    registerTab,
    unregisterTab,
    setIsTabDirty,
    handleTabChange,
    handleClose,
    getEffectiveState,
  };
}
